import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { AbstractControl, FormGroup } from '@angular/forms';

@Component({
    selector: 'app-text-area',
    templateUrl: './text-area.component.html',
    styleUrls: ['../../styles.scss', './text-area.component.scss'],
    standalone: false
})
export class TextAreaComponent implements OnInit, OnDestroy {
  @ViewChild('textarea') input: ElementRef;

  @Input() label: string;
  /** A status pill after the label (e.g. "Autofilled", Figma Status badge 35281:21972); empty = none. */
  @Input() labelBadge = '';
  @Input() placeholder: string;
  @Input() parentFormGroup: FormGroup;
  @Input() controlName: string;
  @Input() error: string;
  @Input() maxLength: string | number = 2000;
  @Input() minLength = '100';
  @Input() showCharCount = false;
  @Input() hideErrorBox = false;
  @Output() checkForErrors = new EventEmitter();

  isRequired = false;
  isDisabled = false;

  // The cap actually put on the <textarea>, resolved once on init. Mobile does
  // not cap these fields and neither does the API, so a stored value can already
  // be longer than maxLength. Enforcing the cap on such a value only freezes the
  // field (the browser refuses every insertion) without shrinking the data, so
  // we drop the cap for that field instead. Normal-length values keep it.
  effectiveMaxLength: string | number = null;

  fieldSubscription: Subscription;

  constructor() {
  }

  // Do not show field as touched and having error when clicked outside the field
  @HostListener('focusout', ['$event'])
  onBlur(event) {
    if (!this.error) {
      this.parentFormGroup.controls[this.controlName].markAsUntouched();
    }
  }

  ngOnInit(): void {
    const control = this.parentFormGroup?.controls[this.controlName];
    this.isDisabled = control?.status === 'DISABLED';
    const currentLength = (control?.value ?? '').toString().length;
    this.effectiveMaxLength = currentLength > Number(this.maxLength) ? null : this.maxLength;
    this.fieldSubscription = control?.valueChanges?.subscribe(() => {
      // If field is marked as touched, mark it as untouched
      if (this.parentFormGroup?.controls[this.controlName]?.touched) {
        this.parentFormGroup?.controls[this.controlName]?.markAsUntouched();
      }

      // Get errors on the form field
      const errors = this.parentFormGroup?.controls[this.controlName]?.errors;

      // If errors exist, handle it
      if (errors) {
        // Clear errors on the form field
        this.parentFormGroup?.controls[this.controlName]?.setErrors(null);

        // Clear out the error messages on the screen (parent component)
        this.checkForErrors?.emit(this.controlName);

        // Add errors again to the form field for showing messages in the future
        this.parentFormGroup?.controls[this.controlName]?.setErrors(errors);
      } else {
        // Clear out the error messages on the screen (parent component)
        this.checkForErrors?.emit(this.controlName);
      }
    });

    // Check if field is required
    if (control?.validator) {
      const validator = control?.validator({} as AbstractControl);
      this.isRequired = !!(validator && validator['required']);
    }
  }

  ngOnDestroy() {
    this.fieldSubscription?.unsubscribe();
  }
}
